import { jsPDF } from "jspdf";

const C = {
  primary: [72, 52, 212],
  primaryDark: [45, 30, 145],
  primarySoft: [239, 236, 255],
  success: [16, 185, 129],
  successSoft: [220, 250, 240],
  warn: [239, 68, 68],
  warnSoft: [254, 226, 226],
  text: [30, 32, 45],
  textLight: [110, 118, 135],
  white: [255, 255, 255],
  border: [225, 229, 240],
  paper: [252, 252, 255],
  gold: [201, 162, 39],
};

const PAGE_W = 297;
const PAGE_H = 210;

const POOL_OPT = [
  "#4834D4", "#5B4CD9", "#6C5CE7", "#7C6EEC",
  "#3A8DFF", "#2FA7DC", "#20B2AA", "#16A085",
];
const POOL_MEJ = [
  "#EF4444", "#F97316", "#F59E0B", "#EAB308",
  "#DB2777", "#BE185D", "#9333EA", "#C026D3",
];

const hexToRgb = (h) => {
  const n = parseInt(h.replace("#", ""), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

const loadImageAsBase64 = (url) =>
  new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      canvas.getContext("2d").drawImage(img, 0, 0);
      resolve({
        data: canvas.toDataURL("image/png"),
        w: img.naturalWidth,
        h: img.naturalHeight,
      });
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });

const drawBackground = (doc) => {
  doc.setFillColor(...C.paper);
  doc.rect(0, 0, PAGE_W, PAGE_H, "F");

  doc.setFillColor(245, 243, 255);
  doc.circle(-10, PAGE_H + 10, 55, "F");
  doc.setFillColor(255, 240, 240);
  doc.circle(PAGE_W + 10, -10, 50, "F");

  doc.setFillColor(...C.primarySoft);
  doc.circle(30, 30, 3, "F");
  doc.setFillColor(...C.warnSoft);
  doc.circle(PAGE_W - 30, PAGE_H - 30, 3, "F");
};

const drawHeader = (doc, logo) => {
  doc.setFillColor(...C.primary);
  doc.rect(0, 0, PAGE_W, 3, "F");

  if (logo) {
    const targetH = 12;
    const targetW = (logo.w / logo.h) * targetH;
    doc.addImage(logo.data, "PNG", 15, 10, targetW, targetH);
  }

  doc.setTextColor(...C.text);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.text("Tus Talentos", PAGE_W / 2, 20, { align: "center" });

  doc.setTextColor(...C.textLight);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text("Descubre tu talento", PAGE_W / 2, 26, { align: "center" });

  const fecha = new Date().toLocaleDateString("es-MX", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
  doc.setTextColor(...C.textLight);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text(fecha, PAGE_W - 15, 15, { align: "right" });
};

const drawColumnHeader = (doc, x, w, label, color, colorSoft) => {
  const y = 38;
  doc.setFillColor(...colorSoft);
  doc.roundedRect(x, y, w, 12, 6, 6, "F");

  doc.setFillColor(...color);
  doc.circle(x + 8, y + 6, 2.4, "F");

  doc.setTextColor(...color);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text(label.toUpperCase(), x + 14, y + 8);
};

const drawTalentCard = (doc, x, y, w, rank, name, color) => {
  const h = 14;

  doc.setFillColor(240, 240, 245);
  doc.roundedRect(x + 0.6, y + 0.6, w, h, 3, 3, "F");

  doc.setFillColor(...C.white);
  doc.setDrawColor(...C.border);
  doc.setLineWidth(0.3);
  doc.roundedRect(x, y, w, h, 3, 3, "FD");

  doc.setFillColor(...color);
  doc.roundedRect(x, y, 3.5, h, 3, 3, "F");
  doc.rect(x + 3, y, 1, h, "F");

  doc.setFillColor(...color);
  doc.circle(x + 12, y + h / 2, 4.8, "F");
  doc.setTextColor(...C.white);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text(String(rank), x + 12, y + h / 2 + 1.4, { align: "center" });

  doc.setTextColor(...C.text);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  const availableWidth = w - 22;
  const displayName = name.trim();
  const nameW = doc.getTextWidth(displayName);
  const truncated =
    nameW > availableWidth
      ? doc.splitTextToSize(displayName, availableWidth)[0]
      : displayName;
  doc.text(truncated, x + 20, y + h / 2 + 1.4);
};

const drawColumn = (doc, x, w, items, pool) => {
  const startY = 55;
  const gap = 4;
  const cardH = 14;
  items.forEach((t, i) => {
    const y = startY + i * (cardH + gap);
    if (y + cardH > PAGE_H - 20) return;
    drawTalentCard(
      doc,
      x,
      y,
      w,
      t.ranking ?? i + 1,
      t.talento_nombre || "",
      hexToRgb(pool[i % pool.length]),
    );
  });
};

const drawFooter = (doc) => {
  const y = PAGE_H - 12;
  doc.setDrawColor(...C.border);
  doc.setLineWidth(0.3);
  doc.line(15, y - 4, PAGE_W - 15, y - 4);

  doc.setTextColor(...C.textLight);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text("Universidad del Talento", 15, y);

  doc.setFont("helvetica", "italic");
  doc.text("Descubre tu talento", PAGE_W - 15, y, { align: "right" });
};

const normalizeArgs = (input) => {
  if (!input) return { talentos: null, esRegistroPublico: false };
  if (input.talentos_optimos || input.talentos_a_mejorar) {
    return { talentos: input, esRegistroPublico: false };
  }
  return {
    talentos: input.talentos,
    esRegistroPublico: input.esRegistroPublico === true,
  };
};

export const generateTalentosPDF = async (input) => {
  const { talentos, esRegistroPublico } = normalizeArgs(input);
  if (!talentos) return null;
  const opt = talentos.talentos_optimos || [];
  const mej = talentos.talentos_a_mejorar || [];

  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });

  const logo = await loadImageAsBase64("/assets/images/logo.png");

  drawBackground(doc);
  drawHeader(doc, logo);

  const marginX = 18;

  if (esRegistroPublico) {
    const colW = Math.min(PAGE_W - marginX * 2, 180);
    const leftX = PAGE_W / 2 - colW / 2;
    drawColumnHeader(doc, leftX, colW, "Dominantes", C.success, C.successSoft);
    drawColumn(doc, leftX, colW, opt, POOL_OPT);
  } else {
    const gap = 8;
    const colW = (PAGE_W - marginX * 2 - gap) / 2;
    const leftX = marginX;
    const rightX = marginX + colW + gap;

    drawColumnHeader(doc, leftX, colW, "Dominantes", C.success, C.successSoft);
    drawColumnHeader(doc, rightX, colW, "No dominantes", C.warn, C.warnSoft);

    drawColumn(doc, leftX, colW, opt, POOL_OPT);
    drawColumn(doc, rightX, colW, mej, POOL_MEJ);
  }

  drawFooter(doc);
  return doc;
};

export const downloadTalentosPDF = async (input) => {
  const doc = await generateTalentosPDF(input);
  if (!doc) return;
  doc.save("Mis-Talentos.pdf");
};

export const previewTalentosPDF = async (input) => {
  const doc = await generateTalentosPDF(input);
  if (!doc) return null;
  return doc.output("bloburl");
};
